import { db } from '../firebase/config';
import { client } from '../hooks/Client';
import { buildStreetLabelMap, streetLabel, nameLabel } from './adressHelpers/buildStreetLabels';
import notDeleted from './notDeleted';

/**
 * @typedef {object} AdressLabels
 * @property {string} description - "Straatnaam Huisnummer, Woonplaats" voor het beschrijvingsveld
 * @property {string} adressLabel - "Straatnaam Huisnummer" voor de agendatitel
 * @property {string} adressName - De naam van de bewoner, volgens dezelfde keten als het huishoudenoverzicht
 */

/**
 * Read the address labels of one household straight from Firestore (not via a
 * hook). Use this at save-time so the labels are correct even if the
 * subscribing component's hook hasn't delivered its first snapshot yet — the
 * bug we hit on mobile / slow net was that `useFirestoreGeneralOrderBy` starts
 * with `""`, so a fast slot-pick produced a description without Huisnummer.
 *
 * Eén lezing voor alle drie de labels: een huisbezoek krijgt bij het aanmaken
 * de beschrijving, het adreslabel én de bewonersnaam op de taak
 * (`docs/patterns.md#data-denormalise-label-onto-response`).
 *
 * De vaste gegevens (straat, nummer, toevoeging, woonplaats, naam) staan op het
 * huishouden zelf; tot de migratie vullen de oude PersonaData-regels aan. Zelfde
 * lezing als het overzicht (`buildStreetLabelMap`), zodat agenda en lijst het
 * eens zijn. AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T7)
 *
 * @param {string} adressId - Adresses.ID (used as PersonaID on PersonaData rows)
 * @returns {Promise<AdressLabels>}
 */
export const getAdressLabels = async (adressId) => {
    if (!adressId || !client) return { description: '', adressLabel: '', adressName: '' };

    const [adressSnap, dataSnap] = await Promise.all([
        db.collection('Adresses')
            .where('CompagnyID', '==', client)
            .where('ID', '==', adressId)
            .get(),
        db.collection('PersonaData')
            .where('CompagnyID', '==', client)
            .where('PersonaID', '==', adressId)
            .get(),
    ]);

    const adresses = adressSnap.docs.map((doc) => doc.data()).filter(notDeleted);
    /** @type {Array<object>} */
    const rows = [];
    dataSnap.forEach((doc) => {
        const data = doc.data();
        if (notDeleted(data)) rows.push({ ...data, PersonaID: adressId });
    });

    const map = buildStreetLabelMap(rows, adresses);
    const streetLine = streetLabel(map, adressId, '');
    const city = (map[adressId] || {}).Woonplaats || '';

    return {
        description: [streetLine, city].filter(Boolean).join(', '),
        adressLabel: streetLine,
        adressName: nameLabel(map, adressId, ''),
    };
};

/**
 * Build the "Straatnaam Huisnummer, Woonplaats" description for an address.
 *
 * @param {string} adressId - Adresses.ID (used as PersonaID on PersonaData rows)
 * @returns {Promise<string>}
 */
const getAdressDescription = async (adressId) => (await getAdressLabels(adressId)).description;

export default getAdressDescription;
